import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OrderModal from '../components/OrderModal';

const TitanPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ title: '', content: '' });
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const location = useLocation();

  // Scroll to hash section on load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [location.hash]);

  // Titan products with Stripe URLs
  const titanProducts = {
    fullSetup: { name: 'Full Setup Service', price: '$75', url: 'https://buy.stripe.com/00weVd35n12xePO0H9bV61u' },
    settingsOnly: { name: 'Settings Only', price: '$75', url: 'https://buy.stripe.com/4gM28rdK17qV7nm0H9bV61t' },
    settingsSetup: { name: 'Full Setup & Settings', price: '$150', url: 'https://buy.stripe.com/3cI00j21j7qVfTS1LdbV61h' },
    cronusScript: { name: 'AFK Zen Script', price: '$40', url: 'https://buy.stripe.com/6oU8wPdK16mR2321LdbV60P' },
    cronusBundle: { name: 'AFK Max Bundle', price: '$75', url: 'https://buy.stripe.com/3cI28r8pH4eJ6ji2PhbV616' },
  };

  const openOrderModal = (productKey) => {
    setSelectedProduct(titanProducts[productKey]);
    setOrderModalOpen(true);
  };

  const hwData = {
    titan: {
      title: "Titan Two Console Tuner",
      content: `<p>The Titan 2 console tuner, as we already discussed, is a mandatory part of your "Titan Setup". Titan Two sits between the controller and your console (Xbox or PlayStation).</p>
                <p class="mt-4">You can find Titan 2's on several different websites like Amazon or ModdedZone.com. The problem is that because of the Titans' burst in popularity its out of stock on most websites. The normal price for a Titan2 is about $140 USD.</p>`
    },
    gpu: {
      title: "High Performance GPU",
      content: `<p>The software uses AI to look at your screen and lock onto enemies. You need a computer that has a Graphics card (GPU). The better the GPU, the better your Titan setup will work overall.</p>
                <p class="mt-4"><strong>Nvidia GPUs:</strong> RTX 5090, 5080, 5070 Ti, 4070 Ti, 4060 Ti, 4060, 3060.</p>
                <p class="mt-4"><strong>AMD GPUs:</strong> RX 9070 XT, 9070, 7900 XTX, 7900 XT, 7700 XT, 7700.</p>
                <p class="mt-4">Smoothness scales with FPS. Low FPS = Less data = Weaker consistency.</p>`
    },
    capture: {
      title: "Capture Card Details",
      content: `<p>It takes the Consoles HDMI video/audio output, and it passes that signal through to your TV/monitor so you can play normally. At the same time, it sends a copy of that video to the PC over USB.</p>
                <p class="mt-4"><strong>Best Choices:</strong><br/>• ElGato 4K Pro (Internal)<br/>• Elgato 4KX, HD60X (External)<br/>• Avermedia GC573</p>
                <p class="mt-4">Easiest way to think about it: a capture card lets the PC "watch" your Xbox or PlayStation.</p>`
    },
    console: {
      title: "Console Compatibility",
      content: `<p>You will need either an Xbox Series X or a PS5. These consoles support 120 FPS, which is mandatory for your Titan setup.</p>
                <p class="mt-4"><strong>Xbox:</strong> Plug directly into the console, no bypass needed.</p>
                <p class="mt-4"><strong>PS5:</strong> Requires a bypass device (XIM Matrix, P5Mate, or FGC2). If you want to play with Mouse and Keyboard on Console, you MUST use an XIM Matrix.</p>`
    }
  };

  const openModal = (key) => {
    setModalData(hwData[key]);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-[#f8fafc] font-['Inter',sans-serif]">
      <Navbar />

      {/* Modal for Hardware Details */}
      {modalOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-lg flex items-center justify-center p-5"
          onClick={closeModal}
          data-testid="hw-modal"
        >
          <div 
            className="bg-[rgba(30,41,59,0.7)] border border-blue-500/50 backdrop-blur-xl max-w-2xl w-full p-8 rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-3xl font-bold text-white italic font-['Poppins',sans-serif]" data-testid="modal-title">
                {modalData.title}
              </h3>
              <button 
                onClick={closeModal} 
                className="text-slate-400 hover:text-white text-2xl"
                data-testid="modal-close"
              >
                &times;
              </button>
            </div>
            <div 
              className="text-slate-300 leading-relaxed space-y-4 text-sm"
              dangerouslySetInnerHTML={{ __html: modalData.content }}
              data-testid="modal-content"
            />
            <button 
              onClick={closeModal} 
              className="mt-8 w-full relative overflow-hidden bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 text-white font-bold py-3 uppercase tracking-widest transition-all duration-300 rounded-xl text-xs shadow-lg shadow-blue-900/50 hover:shadow-blue-500/60 hover:scale-[1.02] border border-blue-400/30"
            >
              <span className="relative z-10">Close Details</span>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section with Background GIF */}
      <header className="relative py-48 md:py-56 overflow-hidden min-h-[500px]">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#0f172a] z-10"></div>
          <video 
            src="/images/titan-gif.webm"
            autoPlay loop muted playsInline
            className="w-full h-full object-cover object-top opacity-40"
          />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-16">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-4 drop-shadow-2xl uppercase tracking-tighter font-['Poppins',sans-serif]" data-testid="titan-hero-title">
            <span className="text-blue-500">TITAN</span> SERVICES
          </h1>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-6 uppercase italic tracking-widest font-['Poppins',sans-serif]">
            Weights, Settings, & Setup Guide
          </p>
          <p className="text-lg md:text-xl text-yellow-400 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
            The gold standard for setting up a 100% safe AI aimbot on console.
          </p>
        </div>
      </header>

      {/* Intro: What is Titan? */}
      <section className="max-w-4xl mx-auto px-6 mb-24 -mt-10 relative z-20">
        <div className="bg-[rgba(30,41,59,0.7)] border border-white/10 backdrop-blur-xl p-8 md:p-10 rounded-3xl shadow-2xl hover:-translate-y-1 hover:border-blue-400 transition-all duration-200">
          <h2 className="text-3xl md:text-4xl mb-6 text-blue-500 tracking-wider font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]">
            Why Choose Us?
          </h2>
          <p className="text-slate-300 leading-relaxed mb-8 text-center">
            AFK Boosts gives customers access to some of the most advanced AI aiming technology available anywhere. When you choose AFK Boosts, you are choosing proven quality, top-tier performance, and a team committed to delivering the best possible results.
          </p>
          <h2 className="text-3xl md:text-4xl mb-6 text-blue-500 tracking-wider font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="what-is-titan">
            What is Titan?
          </h2>
          <p className="text-slate-300 leading-relaxed mb-6">
            When players talk about being "Titan," they are referring to the <span className="text-blue-400 font-semibold">Titan Two Console Tuner</span>. It's a small adapter box that acts as a middleman between your controller and your console or PC. Think of it as a <span className="text-blue-400 font-semibold">programmable translator</span>.
          </p>
          <div className="grid md:grid-cols-2 gap-8 mt-10">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-bold text-xl mb-2 text-white italic font-['Poppins',sans-serif]">vs. Cronus Zen</h3>
              <p className="text-slate-400 text-sm">
                While a Zen runs simple scripts directly on the device, the Titan 2 is used to pass high-speed data from your PC to your controller. This is the key to getting a real AI aimbot on console.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-bold text-xl mb-2 text-white italic font-['Poppins',sans-serif]">How it Works</h3>
              <p className="text-slate-400 text-sm">
                Instead of just running a script, the Titan takes inputs from your computer's AI software and translates them into commands your console understands instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hardware Checklist */}
      <section id="hardware" className="max-w-6xl mx-auto px-6 mb-24">
        <h2 className="text-4xl md:text-5xl text-center mb-4 text-blue-500 tracking-wider font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="hardware-checklist">
          THE HARDWARE CHECKLIST
        </h2>
        <p className="text-center text-lg md:text-xl text-yellow-400 mb-6 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
          Setting up a Titan requires specific hardware. Click each button below to learn more about everything you need to setup your Titan.
        </p>
        <p className="text-center text-cyan-400 text-2xl md:text-3xl mb-8 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Click any component for full technical details</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          <button 
            className="bg-[rgba(30,41,59,0.7)] border border-white/10 backdrop-blur-xl p-6 rounded-2xl flex flex-col text-center hover:-translate-y-1 hover:border-blue-400 transition-all duration-200 cursor-pointer"
            onClick={() => openModal('titan')}
            data-testid="hw-titan"
          >
            <div className="text-blue-400 text-4xl mb-4 font-black italic font-['Poppins',sans-serif]">01</div>
            <h3 className="text-xl font-bold mb-3 font-['Poppins',sans-serif]">Titan Two</h3>
            <p className="text-slate-400 text-xs mb-4 flex-grow italic">A mandatory adapter box. It sits between the controller and the Xbox or PlayStation.</p>
            <span className="text-xs text-blue-400 font-bold uppercase tracking-tighter">Click Here To View Full Specs</span>
          </button>

          <button 
            className="bg-[rgba(30,41,59,0.7)] border border-white/10 backdrop-blur-xl p-6 rounded-2xl flex flex-col text-center hover:-translate-y-1 hover:border-blue-400 transition-all duration-200 cursor-pointer"
            onClick={() => openModal('gpu')}
            data-testid="hw-gpu"
          >
            <div className="text-blue-400 text-4xl mb-4 font-black italic font-['Poppins',sans-serif]">02</div>
            <h3 className="text-xl font-bold mb-3 font-['Poppins',sans-serif]">GPU</h3>
            <p className="text-slate-400 text-xs mb-4 italic">NVIDIA 3060/4060+ or AMD RX 7700+. The AI runs on your PC, so power matters.</p>
            <span className="text-xs text-blue-400 font-bold uppercase tracking-tighter">Click Here To View Full Specs</span>
          </button>

          <button 
            className="bg-[rgba(30,41,59,0.7)] border border-white/10 backdrop-blur-xl p-6 rounded-2xl flex flex-col text-center hover:-translate-y-1 hover:border-blue-400 transition-all duration-200 cursor-pointer"
            onClick={() => openModal('capture')}
            data-testid="hw-capture"
          >
            <div className="text-blue-400 text-4xl mb-4 font-black italic font-['Poppins',sans-serif]">03</div>
            <h3 className="text-xl font-bold mb-3 font-['Poppins',sans-serif]">Capture Card</h3>
            <p className="text-slate-400 text-xs mb-4 italic">Allows your PC to "watch" your console gameplay (Elgato 4K Pro, 4KX, or HD60X).</p>
            <span className="text-xs text-blue-400 font-bold uppercase tracking-tighter">Click Here To View Full Specs</span>
          </button>

          <button 
            className="bg-[rgba(30,41,59,0.7)] border border-blue-500/30 backdrop-blur-xl p-6 rounded-2xl flex flex-col text-center hover:-translate-y-1 hover:border-blue-400 transition-all duration-200 cursor-pointer"
            onClick={() => openModal('console')}
            data-testid="hw-console"
          >
            <div className="text-blue-400 text-4xl mb-4 font-black italic font-['Poppins',sans-serif]">04</div>
            <h3 className="text-xl font-bold mb-3 font-['Poppins',sans-serif]">Console</h3>
            <p className="text-slate-400 text-xs mb-4 italic">Requires Xbox Series X or PS5. 120 FPS is mandatory for a smooth setup.</p>
            <span className="text-xs text-blue-400 font-bold uppercase tracking-tighter">Click Here To View Full Specs</span>
          </button>

        </div>
      </section>

      {/* Setup Support Section */}
      <section id="setup" className="max-w-4xl mx-auto px-6 mb-24">
        <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-blue-500/20 backdrop-blur-xl p-10 rounded-[3rem]">
          <h2 className="text-3xl md:text-4xl mb-2 text-center text-blue-500 tracking-wider font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="setup-section">
            TITAN SETUP & SETTINGS
          </h2>
          <p className="text-lg md:text-xl text-yellow-400 mb-8 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] text-center">Hands-on expert support included</p>
          <div className="bg-[rgba(30,41,59,0.7)] border border-cyan-500 backdrop-blur-xl p-6 md:p-8 rounded-3xl mb-10" data-testid="setup-description">
            <p className="text-slate-300 leading-relaxed mb-4">
              Our Full Setup Service includes a personal, hands-on walkthrough with one of our experts. We help you cable everything correctly, install the software, configure your settings, and dial everything in until your setup is running perfectly.
            </p>
            <p className="text-slate-300 leading-relaxed">
              If you already have a working setup and just need optimized settings and configurations, our Settings Only option gives you custom configs tuned specifically for your hardware and playstyle.
            </p>
          </div>
          <p className="text-center text-lg md:text-xl text-yellow-400 mb-10 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
            Support begins once your purchase is confirmed. <br/>
            This is real, hands-on help — not a "download and figure it out."
          </p>
          
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <ul className="space-y-4 text-sm text-slate-300">
                <li className="flex items-center gap-3"><span className="text-blue-500">✓</span> Pointing you in the correct direction</li>
                <li className="flex items-center gap-3"><span className="text-blue-500">✓</span> Verifying your setup and connections</li>
                <li className="flex items-center gap-3"><span className="text-blue-500">✓</span> Walking you through proper cabling</li>
                <li className="flex items-center gap-3"><span className="text-blue-500">✓</span> Dialing in custom settings & configs</li>
                <li className="flex items-center gap-3"><span className="text-blue-500">✓</span> Staying until everything is perfect</li>
                <li className="flex items-center gap-3"><span className="text-blue-500">✓</span> Optional Video Call Support</li>
              </ul>
              <video 
                src="/images/titan-setup.webm"
                autoPlay loop muted playsInline
                className="w-full mt-6 rounded-2xl border border-blue-500/20"
              />
            </div>

            <div className="space-y-6">
              {/* Full Setup Option */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-blue-400/30 p-6 rounded-2xl flex flex-col gap-4 transition-all duration-200 hover:border-blue-400/60">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block font-bold text-white uppercase tracking-tighter">Full Setup Service</span>
                    <span className="text-[10px] text-slate-400 uppercase">Includes everything</span>
                  </div>
                  <span className="text-2xl font-black text-blue-400">$75</span>
                </div>
                <button onClick={() => openOrderModal('fullSetup')} className="w-full relative overflow-hidden bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 text-white font-bold py-3 text-center uppercase tracking-widest transition-all duration-300 rounded-lg text-xs shadow-lg shadow-blue-900/50 hover:shadow-blue-500/60 hover:scale-[1.02] border border-blue-400/30" data-testid="full-setup-pay">
                  <span className="relative z-10">Order Now</span>
                </button>
              </div>
              
              {/* Settings Only Option */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-cyan-400/30 p-6 rounded-2xl flex flex-col gap-4 transition-all duration-200 hover:border-cyan-400/60">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block font-bold text-white uppercase tracking-tighter">Settings Only</span>
                    <span className="text-[10px] text-slate-400 uppercase">Configuration only</span>
                  </div>
                  <span className="text-2xl font-black text-cyan-400">$75</span>
                </div>
                <button onClick={() => openOrderModal('settingsOnly')} className="w-full relative overflow-hidden bg-gradient-to-r from-cyan-700 via-cyan-600 to-teal-500 text-white font-bold py-3 text-center uppercase tracking-widest transition-all duration-300 rounded-lg text-xs shadow-lg shadow-cyan-900/50 hover:shadow-cyan-500/60 hover:scale-[1.02] border border-cyan-400/30" data-testid="settings-only-pay">
                  <span className="relative z-10">Order Now</span>
                </button>
              </div>

              {/* Full Setup & Settings Option */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-purple-400/30 p-6 rounded-2xl flex flex-col gap-4 transition-all duration-200 hover:border-purple-400/60">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block font-bold text-white uppercase tracking-tighter">Full Setup & Settings</span>
                    <span className="text-[10px] text-slate-400 uppercase">Complete package</span>
                  </div>
                  <span className="text-2xl font-black text-purple-400">$150</span>
                </div>
                <button onClick={() => openOrderModal('settingsSetup')} className="w-full relative overflow-hidden bg-gradient-to-r from-purple-700 via-purple-600 to-violet-500 text-white font-bold py-3 text-center uppercase tracking-widest transition-all duration-300 rounded-lg text-xs shadow-lg shadow-purple-900/50 hover:shadow-purple-500/60 hover:scale-[1.02] border border-purple-400/30" data-testid="settings-setup-pay">
                  <span className="relative z-10">Order Now</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cronus Zen Scripts Section */}
      <section id="cronus" className="max-w-6xl mx-auto px-6 mb-24">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl mb-2 text-blue-500 tracking-wider font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="cronus-section">
            CRONUS ZEN SCRIPTS
          </h2>
          <p className="text-lg md:text-xl text-yellow-400 mb-8 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Premium scripts for enhanced gameplay</p>
          <div className="max-w-4xl mx-auto bg-[rgba(30,41,59,0.7)] border border-cyan-500 backdrop-blur-xl p-6 md:p-8 rounded-3xl mb-8" data-testid="cronus-description">
            <p className="text-slate-300 leading-relaxed mb-4">
              Our AFK Zen Script is the most powerful and refined Cronus Zen script available, giving you a significant competitive edge in-game.
            </p>
            <p className="text-slate-300 leading-relaxed">
              The AFK Max Bundle includes the script plus full setup help from beginning to end — the best value for Cronus Zen users who want the full package.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          <div className="bg-[rgba(30,41,59,0.7)] border border-amber-500 backdrop-blur-xl p-8 rounded-3xl text-center relative flex flex-col hover:-translate-y-1 hover:border-amber-400 transition-all duration-200">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase">Best Seller</div>
            <h3 className="text-2xl font-bold mb-2 uppercase font-['Poppins',sans-serif]">AFK Zen Script</h3>
            <p className="text-slate-400 text-xs mb-6 italic">Includes the current seasons most powerful Cronus Zen Script</p>
            <div className="text-5xl font-black text-amber-400 mb-6">$40</div>
            <div className="mt-auto">
              <button onClick={() => openOrderModal('cronusScript')} className="w-full relative overflow-hidden bg-gradient-to-r from-amber-700 via-amber-600 to-yellow-500 text-white font-bold py-3 px-4 uppercase tracking-widest transition-all duration-300 rounded-lg text-xs shadow-lg shadow-amber-900/50 hover:shadow-amber-500/60 hover:scale-[1.02] border border-amber-400/30" data-testid="cronus-script-pay">
                <span className="relative z-10">Order Now</span>
              </button>
            </div>
          </div>

          <div className="bg-[rgba(30,41,59,0.7)] border border-orange-500 backdrop-blur-xl p-8 rounded-3xl text-center relative flex flex-col hover:-translate-y-1 hover:border-orange-400 transition-all duration-200">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase">Best Value</div>
            <h3 className="text-2xl font-bold mb-2 uppercase font-['Poppins',sans-serif]">AFK Max Bundle</h3>
            <p className="text-slate-400 text-xs mb-6 italic">Includes the current seasons most powerful Cronus Zen Script & Full setup help from beginning to end</p>
            <div className="text-5xl font-black text-orange-400 mb-6">$75</div>
            <div className="mt-auto">
              <button onClick={() => openOrderModal('cronusBundle')} className="w-full relative overflow-hidden bg-gradient-to-r from-orange-700 via-orange-600 to-amber-500 text-white font-bold py-3 px-4 uppercase tracking-widest transition-all duration-300 rounded-lg text-xs shadow-lg shadow-orange-900/50 hover:shadow-orange-500/60 hover:scale-[1.02] border border-orange-400/30" data-testid="cronus-bundle-pay">
                <span className="relative z-10">Order Now</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="max-w-4xl mx-auto px-6 mb-24 text-center">
        <div className="p-8 border-t border-slate-800">
          <h3 className="text-2xl font-bold mb-4 text-blue-300 italic font-['Poppins',sans-serif]">AFK Boosts</h3>
          <p className="text-slate-400 text-sm leading-relaxed max-w-2xl mx-auto">
            We deliver the gold standard. We don't rush—we ensure every new game we add meets the highest quality for settings and services. Apex Legends and COD available now, more coming soon.
          </p>
        </div>
      </section>

      {/* Order Modal */}
      <OrderModal 
        isOpen={orderModalOpen} 
        onClose={() => setOrderModalOpen(false)} 
        product={selectedProduct}
      />

      <Footer />
    </div>
  );
};

export default TitanPage;
